
// Global Lenis instance type (exposed by SmoothScroll component)
declare global { interface Window { __lenis?: import('lenis').default; } }

import React, { useEffect, useState, lazy, Suspense } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Footer from './components/Footer';
import About from './components/About';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import { motion, AnimatePresence } from 'framer-motion';
import OfflineIndicator from './components/OfflineIndicator';

// ── Mobile Desktop Suggestion Toast ──
const DesktopSuggestion: React.FC = () => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[90] md:hidden p-4 rounded-xl flex items-start gap-3 shadow-2xl glass-dark" style={{ border: '1px solid var(--neon-cyan)' }}>
      <div className="text-xl mt-0.5">💻</div>
      <div className="flex-1">
        <h4 className="mono text-xs font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--neon-cyan)' }}>Recommendation</h4>
        <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
          For the best visual experience, please enable <strong>Desktop site</strong> in your browser settings.
        </p>
      </div>
      <button 
        onClick={() => setIsVisible(false)}
        className="p-1 rounded-md opacity-70 hover:opacity-100 transition-opacity"
        style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)' }}
        aria-label="Dismiss recommendation"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};


// Lazy-load below-fold components
const Experience = lazy(() => import('./components/Experience'));
const Skills = lazy(() => import('./components/Skills'));
const Projects = lazy(() => import('./components/Projects'));
const Contact = lazy(() => import('./components/Contact'));
const Certifications = lazy(() => import('./components/Certifications'));
const AboutPage = lazy(() => import('./components/AboutPage'));
const ContactPage = lazy(() => import('./components/ContactPage'));

// ── Loading Fallback ──
const SectionLoader: React.FC = () => (
  <div
    className="flex items-center justify-center py-20"
    style={{ background: 'var(--bg-primary)' }}
  >
    <div className="flex items-center gap-3 mono text-sm" style={{ color: 'var(--neon-green)' }}>
      <span className="cursor-blink" />
      <span>Loading...</span>
    </div>
  </div>
);

import SmoothScroll from './components/shared/SmoothScroll';

function App() {
  const [currentPath, setCurrentPath] = useState(window.location.pathname || '/');

  useEffect(() => {
    // Update page title
    document.title = 'Darshan | Cybersecurity Enthusiast & Developer';

    const handlePopState = () => {
      const newPath = window.location.pathname || '/';
      setCurrentPath(newPath);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentPath]);

  // ── Single-page Cybersecurity Portfolio ──
  const renderHomepage = () => (
    <>
      <ErrorBoundary><Hero /></ErrorBoundary>
      <ErrorBoundary>
        <Suspense fallback={<SectionLoader />}>
          <About showStrategicPillars={false} />
        </Suspense>
      </ErrorBoundary>
      <ErrorBoundary>
        <Suspense fallback={<SectionLoader />}>
          <Experience />
        </Suspense>
      </ErrorBoundary>
      <ErrorBoundary>
        <Suspense fallback={<SectionLoader />}>
          <Skills />
        </Suspense>
      </ErrorBoundary>
      <ErrorBoundary>
        <Suspense fallback={<SectionLoader />}>
          <Projects />
        </Suspense>
      </ErrorBoundary>
      <ErrorBoundary>
        <Suspense fallback={<SectionLoader />}>
          <Contact />
        </Suspense>
      </ErrorBoundary>
    </>
  );

  const renderContent = () => {
    if (currentPath === '/certifications') {
      return (
        <ErrorBoundary>
          <Suspense fallback={<SectionLoader />}>
            <Certifications />
          </Suspense>
        </ErrorBoundary>
      );
    }
    if (currentPath === '/about') {
      return (
        <ErrorBoundary>
          <Suspense fallback={<SectionLoader />}>
            <AboutPage />
          </Suspense>
        </ErrorBoundary>
      );
    }
    if (currentPath === '/contact') {
      return (
        <ErrorBoundary>
          <Suspense fallback={<SectionLoader />}>
            <ContactPage />
          </Suspense>
        </ErrorBoundary>
      );
    }
    return renderHomepage();
  };

  return (
    <SmoothScroll>
      <div className="min-h-screen overflow-x-hidden" style={{ background: 'var(--bg-primary)' }}>

        {/* Skip to main content */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-6 focus:py-3 focus:rounded-lg focus:font-semibold"
          style={{
            background: 'var(--neon-green)',
            color: 'var(--bg-primary)',
          }}
        >
          Skip to main content
        </a>

        <OfflineIndicator />
        <DesktopSuggestion />

        <ErrorBoundary>
          <Navbar />
        </ErrorBoundary>

        <ErrorBoundary>
          <main id="main-content">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentPath}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                {renderContent()}
              </motion.div>
            </AnimatePresence>
          </main>
        </ErrorBoundary>

        <ScrollToTop />

        <ErrorBoundary>
          <Footer />
        </ErrorBoundary>
      </div>
    </SmoothScroll>
  );
}

export default App;
